import {
	HeadContent,
	Outlet,
	Scripts,
	useLocation,
	useMatches,
} from "@tanstack/react-router";

import { lazy, Suspense, useEffect } from "react";

// Lazy load analytics after hydration (non-critical)
const Analytics = lazy(() =>
	import("@vercel/analytics/react").then((m) => ({ default: m.Analytics })),
);
const SpeedInsights = lazy(() =>
	import("@vercel/speed-insights/react").then((m) => ({
		default: m.SpeedInsights,
	})),
);

// Lazy load TanStack Devtools in development only
const TanStackDevtools =
	process.env.NODE_ENV === "production"
		? () => null
		: lazy(() =>
				import("@tanstack/react-devtools").then((res) => ({
					default: res.TanStackDevtools,
				})),
			);

const TanStackRouterDevtoolsPanel =
	process.env.NODE_ENV === "production"
		? () => null
		: lazy(() =>
				import("@tanstack/react-router-devtools").then((res) => ({
					default: res.TanStackRouterDevtoolsPanel,
				})),
			);

import ErrorComponent from "@/components/blocks/error-component";
import NotFound from "@/components/blocks/not-found";
import Header from "@/components/layout/header";

// Lazy load non-critical layout components
const FloatingContact = lazy(
	() => import("@/components/layout/floating-contact"),
);
const ScrollToTop = lazy(() => import("@/components/layout/scroll-to-top"));

import { BASE_URL } from "@/lib/constants";
import LenisProvider from "@/lib/providers/lenis-provider";
import {
	generateLocalBusinessSchema,
	generateOrganizationSchema,
	generateServiceSchema,
	generateWebsiteSchema,
} from "@/lib/utils/schema.utils";
import { getAllServices } from "@/lib/utils/services.utils";
import appCss from "../styles/styles.css?url";

// Lazy load Footer to reduce initial bundle
const Footer = lazy(() => import("@/components/layout/footer"));

export const Route = createRootRoute({
	head: () => ({
		meta: [
			{ charSet: "utf-8" },
			{ name: "viewport", content: "width=device-width, initial-scale=1" },
			{ title: "Craftlayer" },
			{
				name: "description",
				content:
					"Craftlayer is a platform for building and managing your digital presence.",
			},
			// Open Graph / Facebook
			{ property: "og:type", content: "website" },
			{ property: "og:site_name", content: "Craftlayer" },
			{ property: "og:title", content: "Craftlayer" },
			{
				property: "og:description",
				content:
					"Craftlayer is a platform for building and managing your digital presence.",
			},
			{
				property: "og:image",
				content: `${BASE_URL}/og-image.jpg`,
			},
			// Twitter
			{ name: "twitter:card", content: "summary_large_image" },
			{ name: "twitter:site", content: "@craftlayer" },
			{ name: "twitter:title", content: "Craftlayer" },
			{
				name: "twitter:description",
				content:
					"Craftlayer is a platform for building and managing your digital presence.",
			},
			{
				name: "twitter:image",
				content: `${BASE_URL}/og-image.jpg`,
			},
		],
		links: [
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicon-32x32.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "16x16",
				href: "/favicon-16x16.png",
			},
			{ rel: "manifest", href: "/manifest.json" },
			{ rel: "llms-txt", href: "/llms.txt" },
			// Preload critical CSS to reduce blocking time
			{ rel: "preload", href: appCss, as: "style" },
			{ rel: "stylesheet", href: appCss },
			// Preload critical fonts to reduce critical path latency
			{
				rel: "preload",
				href: "/fonts/urbanist-normal-latin.woff2",
				as: "font",
				type: "font/woff2",
				crossOrigin: "anonymous",
			},
			{
				rel: "preload",
				href: "/fonts/syne-latin.woff2",
				as: "font",
				type: "font/woff2",
				crossOrigin: "anonymous",
			},
		],
	}),

	loader: async () => {
		const services = await getAllServices();
		const serviceSchema = generateServiceSchema(
			services.map((s) => ({
				name: s.label,
				description: s.description,
				url: s.href,
			})),
		);

		const organizationSchema = generateOrganizationSchema({
			name: "Craftlayer",
			url: BASE_URL,
			logo: "https://ik.imagekit.io/a2m5mxtn7b/CraftLayer/logo.svg?updatedAt=1764333383003",
		});

		const websiteSchema = generateWebsiteSchema();
		const localBusinessSchema = generateLocalBusinessSchema();

		return {
			serviceSchema,
			organizationSchema,
			websiteSchema,
			localBusinessSchema,
		};
	},
	shellComponent: RootDocument,
	notFoundComponent: NotFound,
	errorComponent: ErrorComponent,
});

import { createRootRoute } from "@tanstack/react-router";
import { JsonLd } from "@/components/seo/json-ld";

function hasBlogData(data: unknown): data is { blog: unknown } {
	return (
		typeof data === "object" &&
		data !== null &&
		"blog" in data &&
		data.blog === null
	);
}

function hasValidityData(data: unknown): data is { isValid: boolean } {
	return typeof data === "object" && data !== null && "isValid" in data;
}

declare global {
	interface Window {
		dataLayer: Record<string, unknown>[];
	}
}

function RootDocument() {
	const matches = useMatches();
	const isNotFoundPage = matches.some((match) => {
		// Check for catch-all route
		if (match.status === "notFound" || match.routeId === "/$") {
			return true;
		}

		// Check if blog route has null blog (invalid slug)
		if (match.routeId === "/blog/$slug" && hasBlogData(match.loaderData)) {
			return true;
		}

		// Check if services route has isValid false (invalid slug)
		if (
			match.routeId === "/services/$slug" &&
			hasValidityData(match.loaderData)
		) {
			if (match.loaderData.isValid === false) {
				return true;
			}
		}

		return false;
	});

	const {
		serviceSchema,
		organizationSchema,
		websiteSchema,
		localBusinessSchema,
	} = Route.useLoaderData();

	// Only show LocalBusiness schema on Home, About, and Contact pages
	const shouldShowSchema = matches.some((match) =>
		["/", "/about", "/contact"].includes(match.routeId),
	);

	const location = useLocation();

	useEffect(() => {
		if (typeof window !== "undefined" && window.dataLayer) {
			window.dataLayer.push({
				event: "page_view",
				page_path: location.pathname,
			});
		}
	}, [location.pathname]);

	return (
		<html lang="en">
			<head>
				<HeadContent />
				{/* Google Tag Manager - Safe: Hardcoded ID, no user input */}
				<script
					dangerouslySetInnerHTML={{
						__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-WM34DNXJ');`,
					}}
				/>
				{/* End Google Tag Manager */}
				<link
					rel="alternate"
					hrefLang="en"
					href={`${BASE_URL}${location.pathname}`}
				/>
				<JsonLd schema={organizationSchema} />
				<JsonLd schema={websiteSchema} />
				<JsonLd schema={serviceSchema} />
				{shouldShowSchema && <JsonLd schema={localBusinessSchema} />}
			</head>
			<body>
				{/* Google Tag Manager (noscript) */}
				<noscript>
					<iframe
						src="https://www.googletagmanager.com/ns.html?id=GTM-WM34DNXJ"
						height="0"
						width="0"
						title="Google Tag Manager"
						style={{ display: "none", visibility: "hidden" }}
					></iframe>
				</noscript>
				{/* End Google Tag Manager (noscript) */}
				{process.env.NODE_ENV === "production" && (
					<Suspense fallback={null}>
						<Analytics />
						<SpeedInsights />
					</Suspense>
				)}
				<LenisProvider>
					{!isNotFoundPage && <Header />}
					<main>
						<Outlet />
					</main>
					{!isNotFoundPage && <Footer />}
					<Suspense fallback={null}>
						<ScrollToTop />
						<FloatingContact />
					</Suspense>
				</LenisProvider>

				{/* Only load devtools in development */}
				{process.env.NODE_ENV === "development" && (
					<TanStackDevtools
						config={{ position: "bottom-right" }}
						plugins={[
							{
								name: "Tanstack Router",
								render: <TanStackRouterDevtoolsPanel />,
							},
						]}
					/>
				)}

				<Scripts />
			</body>
		</html>
	);
}
